import { EnumTypeValue, StationDto } from "@/shared/types";
import { capitalize, capitalizeFirstChar } from "@/shared/utils/strings.ts";

export type StationNameFormat = "stationCodePrefixed" | "stationCodeSuffixed" | "withoutStationCode";

export interface Station {
    code: string;
    korteNaam: string;
    middelNaam: string;
    langeNaam: string;
    landCode: LandCode;
    latitude: string;
    longitude: string;
}

export type StationMetActiviteitSoort = Station & {
    activiteitSoort: "Vertrek" | "Aankomst" | "AankomstEnVertrek" | "Doorkomst";
};

export const Station = {
    withActiviteitSoort(
        station: Station,
        activiteitSoort: "Vertrek" | "Aankomst" | "AankomstEnVertrek" | "Doorkomst"
    ): StationMetActiviteitSoort {
        return {
            ...station,
            activiteitSoort,
        };
    },

    fromDto(dto: StationDto): Station {
        return {
            code: dto.stationCode.toLowerCase(),
            korteNaam: dto.korteNaam,
            middelNaam: dto.middelNaam,
            langeNaam: dto.langeNaam,
            landCode: toLandCode(dto.landCode),
            latitude: dto.breedtegraad,
            longitude: dto.lengtegraad,
        };
    },

    getDisplayName: (station: Station | undefined, format: StationNameFormat) => {
        if (!station) {
            return "";
        }

        const capitalizedNaam = capitalize(station.langeNaam);
        const capitalizedCode = capitalizeFirstChar(station.code);

        switch (format) {
            case "withoutStationCode":
                return capitalizedNaam;
            case "stationCodeSuffixed":
                return `${capitalizedNaam} (${capitalizedCode})`;
            case "stationCodePrefixed":
                return `${capitalizedCode} (${capitalizedNaam})`;
        }
    },

    nlStationsOnly: (stations: Station[]) => stations.filter((s) => s.landCode === "NL"),

    sort: (a: Station, b: Station) => a.langeNaam.localeCompare(b.langeNaam),

    createSortByDisplayName: <T extends Record<K, string>, K extends string = "stationCode">(
        getStationDisplayName: (code: string) => string,
        stationCodeKey: K = "stationCode" as K
    ) => {
        return (a: T, b: T) => getStationDisplayName(a[stationCodeKey]).localeCompare(getStationDisplayName(b[stationCodeKey]));
    },
};

const LandCode = {
    AUSTRIA: "A",
    SWITZERLAND: "CH",
    BELGIUM: "B",
    LUXEMBOURG: "L",
    NETHERLANDS: "NL",
    GERMANY: "D",
    FRANCE: "F",
    ITALY: "I",
    SWEDEN: "S",
    POLAND: "PL",
} as const;
export type LandCode = EnumTypeValue<typeof LandCode>;

function toLandCode(code: string): LandCode {
    return Object.values(LandCode).includes(code as LandCode) ? (code as LandCode) : LandCode.NETHERLANDS;
}
